import React from 'react';
import API from '@aws-amplify/api';
import {
    Box,
    Button,
    Typography,
    Paper,
    Select,
    MenuItem,
    Input,
    InputAdornment,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
} from '@mui/material';
import TextareaAutosize from '@mui/base/TextareaAutosize';
import { HiOutlineArrowNarrowLeft } from 'react-icons/hi';
import { BiColorHeader } from '../scholarshipApplication/components';
import useDetailsWizard from './useDetailsWizard';
import RenderIf from '../../renderIf';
import { useHistory } from 'react-router-dom';
import AttendanceRecord from './attendanceRecord';
import usePrintInvoice from './usePrintInvoice';
import { ShipyardContext } from '../../Context';
import { StatusBox } from '../scholarshipApplication/components';
import PrintToPDF from './printToPDF';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { makeStyles } from '@mui/styles';
import { TbAlertCircle } from 'react-icons/tb';
import { format } from 'date-fns';
import { t } from 'i18next';
import { toast } from 'react-toastify';

const InvoiceDetailsPage = (props) => {
    const history = useHistory();
    const downloadRef = React.useRef();
    const { state, patch } = useDetailsWizard();
    const { userSettings } = React.useContext(ShipyardContext);
    const [editModalIsOpen, setEditModalIsOpen] = React.useState(false);
    const [, setOverrideModalIsOpen] = React.useState(false);
    const [isFetching, setIsFetching] = React.useState(true);
    const [modalStatus, setModalStatus] = React.useState(undefined);
    const [changeReason, setChangeReason] = React.useState('');
    const [forceValidation, setForceValidation] = React.useState(false);
    const classes = useStylesForDetailsModal();

    const isSchoolAdmin = userSettings.UserType === 2;
    const isScholarshipAdmin = userSettings.UserType === 3;
    const routerStateInvoice = props?.location?.state?.invoice;
    const statusRef = props?.location?.state?.statusRef || {};
    const hasEditableFields = isSchoolAdmin && (state.InvoiceStatusID === 1 || state.InvoiceStatusID === 2 || state.InvoiceStatusID === 5);
    const [statusList, setStatusList] = React.useState([]);
    const inputProps = { state, patch };

    const backHandler = () => {
        return history.push({
            pathname: '/finances',
            state: {
                tabConfig: {
                    defaultView: 'invoices',
                },
                listState: props?.location?.state?.listState,
            },
        });
    };

    const getApplication = async (newInvoiceID) => {
        setIsFetching(true);
        let requestBody = {
            ...routerStateInvoice,
        };

        if (newInvoiceID) {
            requestBody.InvoiceID = newInvoiceID;
        }
        try {
            const dynamic = await API.post('ShipyardAPI', `/finances/invoices/${requestBody.AppInstID}`, {
                body: {
                    ...requestBody,
                },
            });
            patch(dynamic?.invoiceData);
        } catch (e) {
            toast.error('General.AjaxError');
        } finally {
            setIsFetching(false);
        }
    };

    const handleOpenModal = async (modalType) => {
        switch (modalType) {
            case 'edit':
                await getPossibleStatuses();
                return setEditModalIsOpen(true);
            case 'override':
                return setOverrideModalIsOpen(true);
            default:
                return;
        }
    };

    const getPossibleStatuses = async () => {
        const res = await API.get('ShipyardAPI', `/finances/invoices/statuses?CurrentStatus=${state.InvoiceStatusID}&InvoiceID=${state.InvoiceID}`);
        const array = Object.keys(res.InvoiceStatuses).map((key) => {
            return {
                label: res.InvoiceStatuses[key].label,
                value: res.InvoiceStatuses[key].id,
            };
        });
        setStatusList(array);
    };

    //#region Edit Status
    const handleSaveEditModal = async () => {
        const { AppInstInvoiceID } = state;

        var id = toast.loading('Changing status...');

        try {
            await API.patch('ShipyardAPI', `/finances/invoices/status_change`, {
                body: {
                    AppInstInvoiceID,
                    StatusID: modalStatus,
                    ChangeReason: changeReason,
                },
            });
            toast.success('Status changed!');
            setEditModalIsOpen(false);
            getApplication();
        } catch (e) {
            toast.error(t(e?.response?.data?.message ? e.response.data.message : 'General.AjaxError'));
        } finally {
            toast.dismiss(id);
        }
    };
    //#endregion Edit Status

    //#region Save
    const handlePageSave = async (fields) => {
        const { AppInstInvoiceID, DaysPresent, DaysInPeriod, InvoiceAmount, AdditionalNotes, AdminNote } = state;

        var id = toast.loading(`${t('General.InteractiveSave')}...`);
        setForceValidation(false);

        try {
            await API.patch('ShipyardAPI', `/finances/invoices/save`, {
                body: {
                    AppInstInvoiceID,
                    DaysPresent,
                    DaysInPeriod,
                    InvoiceAmount,
                    AddNotes: AdditionalNotes,
                    AdminNote,
                    ...fields,
                },
            });

            toast.success(t('General.ChangesSaved'));
        } catch (e) {
            toast.error(t(e?.response?.data?.message ? e.response.data.message : 'General.AjaxError'));
            if (e?.response?.data?.field) setForceValidation(e.response.data.field);
        } finally {
            toast.dismiss(id);
        }
    };
    //#endregion Save

    //#region Submit
    const handleSubmit = async () => {
        const { AppInstInvoiceID, DaysPresent, DaysInPeriod, InvoiceAmount, AdditionalNotes, FileID, AttendanceRecordFileID } = state;

        var id = toast.loading(`${t('General.Submitting')}...`);
        setForceValidation(false);

        try {
            var newInvoiceData = await API.patch('ShipyardAPI', `/finances/invoices/submit`, {
                body: {
                    AppInstInvoiceID,
                    DaysPresent: DaysPresent,
                    DaysInPeriod: DaysInPeriod,
                    InvoiceAmount: InvoiceAmount,
                    AddNotes: AdditionalNotes,
                    AttendanceRecordFileID: !!FileID ? FileID : AttendanceRecordFileID,
                },
            });

            toast.success(t('General.Submitted'));

            getApplication(newInvoiceData.invoiceId);
        } catch (e) {
            if (e?.response?.data?.field) setForceValidation(e.response.data.field);
            else setForceValidation(true);
            toast.error(t(e?.response?.data?.message ? e.response.data.message : 'General.AjaxError'));
        } finally {
            toast.dismiss(id);
        }
    };
    //#endregion Submit

    const hasOverride = false; //TODO fix this.

    React.useEffect(() => {
        getApplication();
        if (!!routerStateInvoice) {
            patch(routerStateInvoice);
        }
    }, []);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
                width: '100%',
                backgroundColor: '#F7F9FC',
            }}
        >
            <Box sx={{ padding: '16px' }}>
                <Box>
                    <Button onClick={backHandler}>
                        <HiOutlineArrowNarrowLeft
                            color="#191919"
                            size={25}
                        />
                        <Typography sx={{ color: '#191919', fontSize: '16px', fontWeight: 600, marginLeft: '8px' }}>
                            {t('Record.BackButtonLabel')}
                        </Typography>
                    </Button>
                </Box>
                <Box sx={{ paddingLeft: '8px' }}>
                    <BiColorHeader
                        left="Quarterly Invoice for"
                        right={`${state.ApplicantFirstName} ${state.ApplicantLastName}.`}
                    />
                    <RenderIf isTrue={!!state.ChangeReason && state.InvoiceStatusID === 5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#FDF1F0', padding: '16px', marginTop: '24px' }}>
                            <TbAlertCircle style={{ fontSize: '24px', fontWeight: 700, color: '#882224' }} />
                            <Typography sx={{ marginLeft: '24px', color: '#882224', fontWeight: 500, fontSize: '15px' }}>
                                Return Reason: {state.ChangeReason}
                            </Typography>
                        </Box>
                    </RenderIf>

                    <RenderIf isTrue={!!state.ChangeReason && state.InvoiceStatusID === 8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#FDF1F0', padding: '16px', marginTop: '24px' }}>
                            <TbAlertCircle style={{ fontSize: '24px', fontWeight: 700, color: '#882224' }} />
                            <Typography sx={{ marginLeft: '24px', color: '#882224', fontSize: '15px' }}>
                                <Box component="span" sx={{ fontWeight: 500, }}>This invoice is not-eligible for payment for the following reason:</Box> {state.ChangeReason}
                            </Typography>
                        </Box>
                    </RenderIf>

                    <Paper sx={{ borderRadius: '16px', marginTop: '24px', padding: '18px 24px' }}>
                        <DetailsSection heading="Status">
                            <Box sx={{ display: 'flex' }}>
                                <Box sx={{ flex: '1', paddingRight: '24px' }}>
                                    <InvoiceLine
                                        label="Status"
                                        value={state?.InvoiceStatus?.label}
                                    >
                                        <StatusBox
                                            status={statusRef[state?.InvoiceStatus]}
                                            justify="flex-end"
                                        />
                                    </InvoiceLine>
                                    <InvoiceLine
                                        label="Invoice #"
                                        value={state?.InvoiceNumber}
                                    />
                                    <InvoiceLine
                                        label="Submitted by"
                                        value={state?.SubmittedBy ? state.SubmittedBy : ''}
                                    />
                                    <InvoiceLine
                                        label="Submitted on"
                                        value={state?.SubmittedDate ? format(new Date(state?.SubmittedDate), 'MM/dd/yyyy') : ''}
                                    />
                                </Box>
                                <Box sx={{ flex: '1' }}>
                                    <RenderIf isTrue={isScholarshipAdmin}>
                                        <Typography>Administrator Notes</Typography>
                                        <TextareaAutosize
                                            disabled={isSchoolAdmin}
                                            aria-label="Administrator Notes"
                                            minRows={3}
                                            onChange={(e) => patch({ AdminNotes: e.target.value })}
                                            value={state.AdminNotes || ''}
                                            placeholder="For administrators only."
                                            style={{ width: '100%', height: '75%' }}
                                        />
                                    </RenderIf>
                                </Box>
                            </Box>
                        </DetailsSection>
                        <DetailsSection heading="Details">
                            <Box sx={{ display: 'flex' }}>
                                <Box sx={{ flex: '1', paddingRight: '24px' }}>
                                    <InvoiceLine
                                        label="Student Name"
                                        value={`${state?.ApplicantFirstName} ${state?.ApplicantLastName}`}
                                    />
                                    <InvoiceLine
                                        label="Student DOB"
                                        value={!!state?.ApplicantDOB ? format(new Date(state?.ApplicantDOB), 'MM/dd/yyyy') : ''}
                                    />
                                    <InvoiceLine
                                        label="Application Approved"
                                        value={!!state.ApprovalDate ? format(new Date(state.ApprovalDate), 'MM/dd/yyyy') : ''}
                                    />
                                    <InvoiceLine
                                        label="Parent/Guardian Name"
                                        value={`${state?.GuardianFirstName || ''} ${state?.GuardianLastName || ''}`}
                                    />
                                    <RenderIf isTrue={isScholarshipAdmin}>
                                        <InvoiceLine
                                            label="Vendor Payee Number"
                                            value={state?.PayeeNumber}
                                        />
                                    </RenderIf>
                                </Box>
                                <Box sx={{ flex: '1' }}>
                                    <RenderIf isTrue={isScholarshipAdmin}>
                                        <InvoiceLine
                                            label="Location"
                                            value={state?.Location}
                                        />
                                    </RenderIf>
                                    <InvoiceLine
                                        label="Reporting Period"
                                        value={state?.Period}
                                    />
                                    <InvoiceLine
                                        label="Quarter Date From"
                                        value={!!state.PeriodStartUTC ? format(new Date(state.PeriodStartUTC), 'MM/dd/yyyy') : ''}
                                    />
                                    <InvoiceLine
                                        label="Quarter Date To"
                                        value={!!state.PeriodEndUTC ? format(new Date(state.PeriodEndUTC), 'MM/dd/yyyy') : ''}
                                    />
                                    <InvoiceLine
                                        label="Withdrawal Date"
                                        value={!!state.WithdrawalDate ? format(new Date(state.WithdrawalDate), 'MM/dd/yyyy') : 'N/A'}
                                    />
                                </Box>
                            </Box>
                        </DetailsSection>
                        <DetailsSection heading="Description">
                            <RenderIf isTrue={isScholarshipAdmin || !hasEditableFields}>
                                <InvoiceReadOnlyDescription
                                    state={state}
                                    patch={patch}
                                    setOverrideModalIsOpen={setOverrideModalIsOpen}
                                    hasOverride={hasOverride}
                                    isFetching={isFetching}
                                    isScholarshipAdmin={isScholarshipAdmin}
                                />
                            </RenderIf>
                            <RenderIf isTrue={isSchoolAdmin && hasEditableFields}>
                                <EditableDescriptionSection
                                    inputProps={inputProps}
                                    isFetching={isFetching}
                                    forceValidation={forceValidation}
                                />
                            </RenderIf>
                        </DetailsSection>
                    </Paper>
                </Box>
            </Box>
            <Box
                sx={{
                    boxShadow: '6px -2px 13px 1px rgba(188,188,188, 0.37)',
                    backgroundColor: 'rgba(247,249,252, 1)',
                    height: '60px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    paddingRight: '24px',
                    position: 'sticky',
                    bottom: 0,
                    gap: '1rem',
                }}
            >
                <RenderIf isTrue={isScholarshipAdmin || (isSchoolAdmin && state.InvoiceStatusID !== 2 && state.InvoiceStatusID !== 5)}>
                    <FooterMenu
                        handleOpenModal={handleOpenModal}
                        state={state}
                        patch={patch}
                        downloadRef={downloadRef}
                        isFetching={isFetching}
                        isScholarshipAdmin={isScholarshipAdmin}
                    />
                </RenderIf>

                <RenderIf isTrue={isSchoolAdmin && (state.InvoiceStatusID === 2 || state.InvoiceStatusID === 5)}>
                    <Button
                        type="primary"
                        sx={(theme) => {
                            return {
                                backgroundColor: theme.palette.primary.main,
                                color: '#fff',
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.main,
                                },
                            };
                        }}
                        onClick={() => handleSubmit()}
                        disabled={isFetching}
                    >
                        Submit invoice
                    </Button>
                </RenderIf>
                {/* #TODO on hover it would change the background to white so without having to rework all the buttons in this component I hardcoded it to stay the same color on hover */}
                <Button
                    type="primary"
                    sx={(theme) => {
                        return {
                            backgroundColor: theme.palette.primary.main,
                            color: '#fff',
                            '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                            },
                        };
                    }}
                    onClick={() => handlePageSave({ AdminNotes: state.AdminNotes })}
                    disabled={isFetching}
                >
                    Save Changes
                </Button>
            </Box>
            <Dialog
                open={editModalIsOpen}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <DialogTitle>Edit invoice status</DialogTitle>
                <DialogContent>
                    <Box>
                        <Typography className={classes.modalContent} sx={{ '& b': {
                            color: theme=> theme.palette.primary.main,
                        }}}>
                            Select a new status for the invoice(s), and click on <b>Update Status</b>. Depending on the new status you may be required to complete the "Reason for Change" field.
                        </Typography>
                        <Typography className={classes.modalLabel}>
                            <span className={classes.required}>*</span>Status:
                        </Typography>
                        <Select
                            className={classes.modalSelect}
                            labelId="status-select"
                            sx={{
                                width: 'calc(170rem/16)',
                                mb: 'calc(20rem/16)',
                                '& > div': {
                                    width: '100%',
                                    padding: 'calc(8rem/16) calc(9rem/16)',
                                },
                            }}
                            value={modalStatus}
                            onChange={(e) => setModalStatus(e.target.value)}
                        >
                            {statusList.map((each) => (
                                <MenuItem value={each.value}>{t(each.label)}</MenuItem>
                            ))}
                        </Select>

                        <RenderIf isTrue={modalStatus === 5 || modalStatus === 8}>
                            <Typography className={classes.modalLabel}>
                                <span className={classes.required}>*</span>Reason for Change:
                            </Typography>
                            <TextField
                                aria-label="Return reason"
                                multiline
                                minRows={3}
                                value={changeReason}
                                onChange={(e) => setChangeReason(e.target.value)}
                                style={{ width: '98%' }}
                                className={classes.modalTextArea}
                            />
                        </RenderIf>
                    </Box>

                    <Box>
                        <Button
                            className={`${classes.modalActionBtn} ${classes.cancelBtn}`}
                            onClick={() => setEditModalIsOpen(false)}
                        >
                            <Typography>Cancel</Typography>
                        </Button>
                        <Button
                            variant="contained"
                            className={`${classes.modalActionBtn} ${classes.saveBtn}`}
                            onClick={() => handleSaveEditModal()}
                        >
                            <Typography>Update Status</Typography>
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
            {/* <Modal
        open={overrideModalIsOpen}
        aria-labelledby="invoice-override-amount"
        aria-describedby="invoice-override-amount"
      >
        <Box sx={modalStyle}>
          <Typography>Override invoice amount</Typography>
          <Typography>Enter the override amount and the reason below.  The override amount will replace the Quarterly Check Amount as the invoice total.</Typography>
          <Typography>Quarterly Check Amount:</Typography>
          <Input type="text" disabled value={'1'} onChange={() => {}} />
          <Typography>Override Amount:</Typography>
          <Input type="text"  value={'2'} onChange={() => {}} />
          <Typography>Override reason:</Typography>
          <TextareaAutosize
            aria-label="Administrator Notes"
            minRows={3}
            style={{ width: '100%', height: '92%' }}
          />
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
            <Box>
              <Button onClick={() => handleOverrideDelete(false)}><Typography>Delete</Typography></Button>
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
              <Button onClick={() => setOverrideModalIsOpen(false)}><Typography>Cancel</Typography></Button>
              <Button onClick={() => handleSaveEditModal()}><Typography>Save Changes</Typography></Button>
            </Box>
          </Box>
        </Box>
      </Modal> */}
        </Box>
    );
};

export default InvoiceDetailsPage;

export const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '50vw',
    maxWidth: '600px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: '16px',
};

const DetailsSection = ({ heading, children }) => {
    return (
        <Box sx={{ padding: '16px' }}>
            <Typography
                variant="h4"
                sx={{ fontSize: '18px', color: '#1F4E92', fontWeight: 700 }}
            >
                {heading}
            </Typography>
            <Box sx={{ padding: '8px' }}>{children}</Box>
        </Box>
    );
};

const InvoiceLine = ({ value, label, children, subLabel, amountField = false }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                borderBottom: '1px solid #DBDFED',
                paddingBottom: '8px',
                paddingTop: '8px',
            }}
        >
            <Box>
                <Typography sx={{ color: '#586874', fontSize: '14px', fontWeight: 400 }}>{label}</Typography>
                {subLabel}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <RenderIf isTrue={!!value || value === 0}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 400, color: '#231C32' }}>
                        <RenderIf isTrue={amountField}>$&nbsp;</RenderIf>
                        {amountField ? value?.toFixed?.(2) : value}
                    </Typography>
                </RenderIf>
                <RenderIf isTrue={!!children}>{children}</RenderIf>
            </Box>
        </Box>
    );
};

const FooterMenu = ({ handleOpenModal, state, patch, downloadRef, isFetching, isScholarshipAdmin }) => {
    const [selectIsRendered, setSelectIsRendered] = React.useState(false);
    const { printInvoice } = usePrintInvoice();
    const classes = useStylesForDetailsModal();
    const curriedCleanup = (type) => {
        setSelectIsRendered(false);
        return () => handleOpenModal(type);
    };
    return (
        <Box sx={{ position: 'relative' }}>
            <Box
                sx={{
                    position: 'absolute',
                    top: isScholarshipAdmin ? '-100px' : '-75px',
                    right: 0,
                    display: selectIsRendered ? 'flex' : 'none',
                    backgroundColor: '#FFFFFF',
                    padding: '16px',
                    width: '150px',
                    flexDirection: 'column',
                    boxShadow: '6px -6px 13px 1px rgba(188,188,188, 0.47)',
                    '& .MuiButton-root': {
                        justifyContent: 'flex-start',
                        p: theme=> theme.spacing(1),
                    }
                }}
            >
                <RenderIf isTrue={isScholarshipAdmin}>
                    <Button onClick={() => curriedCleanup('edit')()}>
                        <Typography className={classes.menuItem}>Edit Status</Typography>
                    </Button>
                </RenderIf>
                {/* <Button onClick={() => curriedCleanup('override')()}><Typography className={classes.menuItem}>Add Override</Typography></Button> */}
                <Button
                    onClick={() => printInvoice(state, patch, downloadRef)}
                    disabled={state?.InvoiceStatusID <= 2 || state?.InvoiceStatusID === 8}
                >
                    <Typography className={classes.menuItem} disabled={state?.InvoiceStatusID <= 2 || state?.InvoiceStatusID === 8}>Print Invoice</Typography>
                </Button>
                <PrintToPDF
                    state={state}
                    ref={downloadRef}
                />
            </Box>
            <Button
                sx={{ border: '2px solid #014E8D', padding: '8px 16px', paddingRight: '12px' }}
                onClick={() => setSelectIsRendered(!selectIsRendered)}
                disabled={isFetching}
            >
                <Typography sx={{ borderRight: '2px solid #014E8D', paddingRight: '12px', marginRight: '8px', fontSize: '13px' }}>
                    Select Action
                </Typography>
                <MdKeyboardArrowDown
                    style={{
                        transition: 'ease transform .3s',
                        transform: `rotate(${selectIsRendered ? '180deg' : '0'})`,
                        fontSize: '20px',
                    }}
                />
            </Button>
        </Box>
    );
};

export const useStylesForDetailsModal = makeStyles((theme) => ({
    menuItem: {
        fontSize: '15px',
        color: '#3C3E45',
        transition: 'ease-in all .3s',
        '&:hover': {
            color: '#3763E8',
        },
        '&[disabled]': {
            color: theme.palette.grey['border'],
        }
    },
    modalTitle: {
        color: '#0F428C',
        marginBottom: '12px',
    },
    modalContent: {
        color: '#6D7278',
        fontSize: '1rem',
        fontWeight: 400,
        marginBottom: '16px',
    },
    modalLabel: {
        padding: '6px 0',
        marginTop: '16px',
    },
    modalSelect: {},
    modalTextArea: {},
    modalHeader: {
        borderBottom: '1px solid #979797',
        paddingBottom: '1em',
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '24px',
    },
    modalWrapper: {
        width: '75%',
        margin: '0 auto',
        padding: '24px 0',
    },
    required: {
        color: '#D24444',
        fontSize: '18px',
        marginRight: '2px',
    },
    modalActionsWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        marginTop: '36px',
    },
    modalActionBtn: {
        padding: '8px 16px',
        marginLeft: '16px',
    },
    saveBtn: {
        color: '#FFFFFF',
        backgroundColor: '#014E8D',
        border: '2px solid #014E8D',
        '&&:hover': {
            backgroundColor: '#002E54',
        },
    },
    cancelBtn: {
        backgroundColor: '#E2E6E9',
        color: '#3c3e45',
        border: '2px solid #E2E6E9',
    },
    editableInput: {
        border: '1px solid rgba(73, 79, 249, 0.39)',
        padding: '0',
        paddingLeft: '8px',
        marginTop: '8px',
        width: '100px',
        fontSize: '15px',
        fontWeight: 400,
        '&&&::before': {
            border: 'none',
            borderBottomStyle: 'none',
        },
        '&&&::after': {
            border: 'none',
            borderBottomStyle: 'none',
        },
    },
    editableInputWrapper: {
        marginTop: '16px',
        paddingBottom: '16px',
    },
    submitBtn: {
        backgroundColor: '#F7F9FC',
        border: '2px solid #014E8D',
        '&&:hover': {
            backgroundColor: '#ACAEB0',
        },
    },
    failedValidation: {
        border: '1px solid #D24444',
    },
    failText: {
        color: '#D24444',
        fontSize: '14px',
    },
}));

const InvoiceReadOnlyDescription = ({ state, patch, isFetching, setOverrideModalIsOpen, hasOverride, isScholarshipAdmin }) => {
    return (
        <Box sx={{ display: 'flex' }}>
            <Box sx={{ flex: '1', paddingRight: '24px' }}>
                <InvoiceLine
                    label="Quarterly Private School Tuition and Fees"
                    value={state?.TuitionAndFees}
                    amountField={true}
                />
                <InvoiceLine
                    label="Quarterly LNH Award"
                    value={state?.LNHAward}
                    subLabel={
                        <RenderIf isTrue={hasOverride}>
                            <Typography>(Override applied)</Typography>
                        </RenderIf>
                    }
                    amountField={true}
                >
                    <RenderIf isTrue={hasOverride}>
                        <Typography onClick={() => setOverrideModalIsOpen(true)}>Edit</Typography>
                    </RenderIf>
                </InvoiceLine>
                <InvoiceLine
                    label="Quarterly Check Amount"
                    value={state?.InvoiceAmount}
                    amountField={true}
                />
                <InvoiceLine
                    label="Number of Days in Private School Quarter"
                    value={state?.DaysInPeriod}
                />
                <InvoiceLine
                    label="Number of Days Student was Present"
                    value={state?.DaysPresent}
                />
            </Box>
            <Box sx={{ flex: '1' }}>
                <Typography>School's Notes</Typography>
                <TextareaAutosize
                    disabled={isScholarshipAdmin}
                    aria-label="Additional Notes"
                    minRows={3}
                    onChange={(e) => patch({ AdditionalNotes: e.target.value })}
                    value={state.AdditionalNotes || ''}
                    placeholder="Please write any additional notes here"
                    style={{ width: '100%', minHeight: '70px' }}
                />
                <Box sx={{ marginTop: '24px' }}>
                    <AttendanceRecord
                        detailWizard={{ state, patch }}
                        isFetching={isFetching}
                        downloadOnly={true}
                    />
                </Box>
            </Box>
        </Box>
    );
};

const EditableDescriptionSection = ({ inputProps, isFetching, forceValidation }) => {
    const { state, patch } = inputProps;

    return (
        <Box sx={{ display: 'flex' }}>
            <Box sx={{ flex: '1', paddingRight: '24px' }}>
                <EditableInputTextField
                    {...inputProps}
                    stateKey={'TuitionAndFees'}
                    label="Quarterly Private School Tuition and Fees"
                    disabled
                    amountField={true}
                />
                <EditableInputTextField
                    {...inputProps}
                    stateKey={'LNHAward'}
                    label="Quarterly LNH Award"
                    disabled
                    amountField={true}
                />
                <EditableInputTextField
                    {...inputProps}
                    stateKey={'InvoiceAmount'}
                    label="Quarterly Check Amount"
                    required
                    forceValidation={forceValidation}
                    amountField={true}
                    validator="^[0-9]*[.]{0,1}[0-9]*$"
                />
                <Typography>School's Notes</Typography>
                <TextareaAutosize
                    aria-label="Additional Notes"
                    minRows={3}
                    onChange={(e) => patch({ AdditionalNotes: e.target.value })}
                    value={state.AdditionalNotes || ''}
                    placeholder="Please write any additional notes here"
                    style={{ width: '90%', minHeight: '40px', marginTop: '8px', padding: '8px' }}
                />
            </Box>
            <Box sx={{ flex: '1' }}>
                <EditableInputTextField
                    {...inputProps}
                    stateKey={'DaysInPeriod'}
                    label="Number of Days in Private School Quarter"
                    required
                    forceValidation={forceValidation}
                    disabled
                    validator="^[0-9]*[.]{0,1}[0-9]*$"
                />
                <EditableInputTextField
                    {...inputProps}
                    stateKey={'DaysPresent'}
                    label="Number of Days Student was Present"
                    required
                    forceValidation={forceValidation}
                    validator="^[0-9]*[.]{0,1}[0-9]*$"
                />
                <Box sx={{ marginTop: '24px' }}>
                    <AttendanceRecord
                        detailWizard={{ state, patch }}
                        isFetching={isFetching}
                        forceValidation={forceValidation}
                    />
                </Box>
            </Box>
        </Box>
    );
};
const EditableInputTextField = (props) => {
    const { state, patch, stateKey, label, disabled, required, amountField = false, forceValidation, validator = '.*' } = props;

    const classes = useStylesForDetailsModal();

    const failedValidation =
        !RegExp(validator).test(state[stateKey]) || state[stateKey] === '' || state[stateKey] === 0 || state[stateKey] === null;

    const displayInvalid = (forceValidation === true || forceValidation === stateKey) && failedValidation;

    return (
        <Box className={classes.editableInputWrapper}>
            <Typography className={displayInvalid ? classes.required : null}>
                <span
                    className={classes.required}
                    hidden={!required}
                >
                    *
                </span>
                {label}
            </Typography>
            <Input
                sx={{ backgroundColor: disabled ? '#F5F7FF' : '#FFFFFF' }}
                className={[classes.editableInput, displayInvalid ? classes.failedValidation : null]}
                disabled={disabled}
                value={state[stateKey]}
                onChange={(e) => patch({ [stateKey]: e.target.value })}
                startAdornment={amountField ? <InputAdornment position="start">$</InputAdornment> : null}
            />

            <RenderIf isTrue={displayInvalid}>
                <Typography className={classes.failText}>{`(${label} is required.)`}</Typography>
            </RenderIf>
        </Box>
    );
};

